<template>
  <div id="app">
    <BToaster name="router-link-toaster" class="router-link-toaster" />

    <BButton v-if="debug" @click="$router.push({ name: 'Debug' })">
      デバッグメニュへ
    </BButton>
    <BButton v-if="debug" to="" @click="$router.back()">
      戻る
    </BButton>
    <div>
      <RouterView />
    </div>
    <div v-if="!!environment && environment !== 'prod'" id="app-environment">
      {{ environment }}
    </div>
    <div
      v-if="!!environment && environment !== 'prod'"
      id="app-product"
      :class="[`product-${product.slug}`]"
    >
      {{ product.name }}
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-facing-decorator";

import { registerAppUpdator } from "@/app-updator";
import { accountService } from "@/app/services/AccountService";
import { maintenanceService } from "@/app/services/MaintenanceService";
import { versionService } from "@/app/services/VersionService";
import { VUE_APP_ENV } from "@/config/VueAppEnv";
import {
  initializeAdjust,
  requestTrackingAuthorizationWithCompletionHandler
} from "@/plugins/adjust";
import { captureException } from "@/plugins/sentry";
import { applicationStore } from "@/store/application";
import { authStore } from "@/store/auth";
import { debugModule } from "@/store/debug";
import { deviceModule } from "@/store/device";
import { fcmMessageModule } from "@/store/fcm-message";
import { maintenanceStore } from "@/store/maintenance";
import { routerPush } from "@/utils/routerPush";
import { routerReplace } from "@/utils/routerReplace";
import { systemService } from "./app/services/SystemService";
import { Application } from "./types/order/Application";

@Component
export default class App extends Vue {
  maintenanceIntervalId: number | null = null;
  applicationIntervalId: number | null = null;

  get debug() {
    return debugModule.debug;
  }
  get isInMaintenance() {
    return maintenanceStore.isInMaintenance;
  }
  get environment() {
    return VUE_APP_ENV;
  }
  get product() {
    return authStore.product;
  }

  get notAttendedAndCorrectedApplications() {
    return applicationStore.notAttendedAndCorrectedApplications;
  }

  get notLeavingWorkApplications() {
    return applicationStore.notLeavingWorkApplications;
  }

  @Watch("notAttendedAndCorrectedApplications")
  async onChangeNotAttendedAndCorrectedApplications(
    applications: Application[]
  ) {
    if (
      applications.length === 0 ||
      this.ignoreAttendanceCheckPages.includes(String(this.$route.name))
    ) {
      return;
    }
    await this.$router.push({ name: "ApplicationsList" });
  }

  @Watch("notLeavingWorkApplications")
  async onChangeNotLeavingWorkApplications(applications: Application[]) {
    if (
      applications.length === 0 ||
      this.ignoreAttendanceCheckPages.includes(String(this.$route.name))
    ) {
      return;
    }
    await this.$router.push({ name: "ApplicationsList" });
  }

  @Watch("applicationToRemind")
  async onChangeApplicationToRemind(application: Application | null) {
    if (!application) {
      return;
    }
    if (!this.applicationRemindRouteNames.includes(String(this.$route.name))) {
      return;
    }
    await this.$router.push({ name: "Search" });
  }

  get ignoreAttendanceCheckPages() {
    return [
      "AttendanceCorrection",
      "AttendanceCorrectionConfirmation",
      "AttendanceCorrectionFinished",
      "QrScanner",
      "QrScannerWeb",
      "OrdersPreview"
    ];
  }

  get applicationRemindRouteNames() {
    return ["MessageRooms", "Mypage", "FavList", "ApplicationsList"];
  }

  logEvent(event: string) {
    const routeName = this.$route.name! as string;
    const e = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      route_name: routeName || "",
      event,
      // @ts-ignore
      hasCordova: !!window.cordova
    };
    systemService.log(e);
  }

  async created() {
    // デバイス情報を使うので初期化
    try {
      await deviceModule.setDeviceInfo();
    } catch (e) {
      captureException(e as Error);
    }
    // device情報をセットしたら起動ログを記録
    this.logEvent("AppCreated");

    try {
      const isMeetRequirements = await versionService.isMeetRequirements();
      if (!isMeetRequirements) {
        await this.$bvModal.msgBoxOk(
          "新しいバージョンがリリースされています。アプリをアップデートしてください。",
          {
            centered: true
          }
        );
        await this.$router.replace({ name: "VersionsRequireUpdate" });
        return;
      }
    } catch (e) {
      captureException(e as Error);
    }

    // 更新検知
    try {
      registerAppUpdator();
    } catch (e) {
      captureException(e as Error);
    }

    // App横断トラッキング（ATT）の許可ダイアログ表示
    try {
      await requestTrackingAuthorizationWithCompletionHandler();
    } catch (e) {
      captureException(e as Error);
    }

    try {
      // カメラの使用許可
      // @ts-ignore
      const cordova = window.cordova;
      // 3.14.0以前はpermissionsがない
      if (cordova && cordova.plugins && cordova.plugins.permissions) {
        const permissions = cordova.plugins.permissions;
        // @ts-ignore
        permissions.checkPermission(permissions.CAMERA, status => {
          if (status.hasPermission) {
            return;
          }
          // @ts-ignore
          permissions.requestPermission(permissions.CAMERA);
        });
      }
    } catch (e) {
      captureException(e as Error);
    }

    // Adjustの初期化
    // NOTE: import 時に実行する形だと、"Native: tried calling Adjust.create, but the Adjust plugin is not installed."エラーが発生する
    // NOTE: Adjustサポートからの回答で、iOSでインストールを正しく計測するにはATTの許可ダイアログ表示後に初期化
    try {
      initializeAdjust();
    } catch (e) {
      captureException(e as Error);
    }

    // fcm初期化
    try {
      fcmMessageModule.init();
    } catch (e) {
      captureException(e as Error);
    }

    // ログイン済みの場合、起動に関する情報を記録
    try {
      // @ts-ignore
      const cordova = window.cordova;
      if (cordova && authStore.user) {
        await accountService.recordAppBoot();
      }
      // ODM の初期設定
      authStore.initiateOnDeviceConversionMeasurement();
    } catch (e) {
      // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
      captureException(e as Error);
    }

    // ログイン済みの場合、UUIDが登録されていなければ登録処理を行う
    // ワーカーやコンソールなどアプリを跨いだABテストで使用するためのUUID
    try {
      if (authStore.user) {
        await accountService.registerUuidIfNeeded();
      }
    } catch (e) {
      captureException(e as Error);
    }

    try {
      await Promise.all([
        this.fetchApplications(),
        this.fetchIsInMaintenance()
      ]);
    } catch (e) {
      captureException(e as Error);
    }

    try {
      // ポーリング処理を開始
      await this.startPolling();
    } catch (e) {
      captureException(e as Error);
    }
  }

  unmounted() {
    this.stopPolling();
  }

  async fetchIsInMaintenance() {
    const oldIsInMaintenance = maintenanceStore.isInMaintenance;
    const newIsInMaintenance = await maintenanceService.fetchIsInMaintenance();
    maintenanceStore.SET_IS_IN_MAINTENANCE(newIsInMaintenance);

    if (newIsInMaintenance) {
      // メンテナンス中の場合
      routerPush(this, { name: "Maintenance" });
      return;
    } else if (
      oldIsInMaintenance ||
      String(this.$route.name) === "Maintenance"
    ) {
      // メンテナンス中からの復帰の場合
      authStore.logout();
      await this.$bvModal.msgBoxOk(
        "メンテナンスが完了しました。\n再度ログインしてください。",
        {
          centered: true
        }
      );
      routerReplace(this, { name: "Welcome" });
    }
  }

  startPolling() {
    // メンテナンス情報
    this.maintenanceIntervalId = window.setInterval(
      this.fetchIsInMaintenance,
      300000
    );

    // アプリケーション情報
    this.applicationIntervalId = window.setInterval(() => {
      this.fetchApplications();
    }, 300000);

    // アプリがフォアグラウンドに復帰した場合
    document.addEventListener("resume", async () => {
      await this.fetchIsInMaintenance();
      await this.fetchApplications();
    });
  }

  async fetchApplications() {
    if (authStore.user) {
      await applicationStore.fetchApplications();

      // 未出勤・未退勤の勤務がある場合は、お仕事管理画面に遷移
      // お仕事管理画面で未出勤・未退勤確認モーダルが表示される
      if (
        (applicationStore.notAttendedAndCorrectedApplications.length > 0 ||
          applicationStore.notLeavingWorkApplications.length > 0) &&
        !this.ignoreAttendanceCheckPages.includes(String(this.$route.name))
      ) {
        routerPush(this, { name: "ApplicationsList" });
      }
    }
  }

  stopPolling() {
    if (this.maintenanceIntervalId) {
      clearInterval(this.maintenanceIntervalId);
    }
    if (this.applicationIntervalId) {
      clearInterval(this.applicationIntervalId);
    }
  }
}
</script>

<style lang="scss">
#app-environment {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  margin: 0;
  background: yellow;
}

#app-product {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10000;
  margin: 0;
  background: yellow;
}
</style>

<style lang="scss" scoped>
.router-link-toaster {
  position: fixed;
  right: 0.5rem;
  left: 0.5rem;
  height: 0;
  padding: 0;
  margin: 0;
  margin-top: calc(env(safe-area-inset-top, 0px) + 110px);
  overflow: visible;

  :deep(.b-toaster-slot) {
    top: 0.5rem;
  }

  :deep(.b-toaster-slot),
  :deep(.b-toaster-slot) .b-toast,
  :deep(.b-toaster-slot) .b-toast .toast {
    width: 100%;
    max-width: 100%;
  }
}
</style>
